
// src/services/testService.js

import axios from 'axios';

const API_BASE_URL = 'https://exam.doode.in/api'; 

/**
 * Checks if the JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or invalid
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded.exp < Math.floor(Date.now() / 1000);
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Consider invalid tokens as expired
  }
};

/**
 * Handles token expiry by clearing localStorage and redirecting to login
 */
const handleTokenExpiry = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('student');
  localStorage.removeItem('id');
  alert('Session expired. Please log in again.');
  window.location.href = '/student-login';
};

/**
 * Gets authorization headers with JWT token
 * @returns {Object} Headers object with Authorization
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  
  if (isTokenExpired(token)) {
    console.warn("Token expired, redirecting to login...");
    handleTokenExpiry();
    return null;
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Generate a test for the student.
 * If the user has exhausted the free tests, the backend will respond with `paymentRequired: true`.
 * @param {string} studentId 
 * @returns {Object} Response data
 */
export const generateTest = async (studentId, departmentId, timeDuration) => {
  try {
    console.log('testService - generateTest called with:', { studentId, departmentId, timeDuration });
    console.log('testService - timeDuration type:', typeof timeDuration);
    
    const response = await fetch('/api/tests/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        studentId, 
        departmentId, 
        timeDuration: parseInt(timeDuration) || 60 // Ensure it's a number with fallback
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    console.log('testService - received result:', result);
    return result;
  } catch (error) {
    console.error('testService - generateTest error:', error);
    throw error;
  }
};

/**
 * Complete the test with given results.
 * @param {string} testId 
 * @param {Object} results 
 * @returns {Object} Response data
 */
export const completeTest = async (testId, results) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.post(
      `${API_BASE_URL}/test/complete`, 
      { testId, results },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error completing test:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to complete test');
  }
};

/**
 * Get upcoming tests for the student.
 * @param {string} studentId 
 * @returns {Array} List of upcoming tests
 */
export const getUpcomingTests = async (studentId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.get(
      `${API_BASE_URL}/test/upcoming/${studentId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming tests:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to fetch upcoming tests');
  }
};

/**
 * Get test details by its ID.
 * @param {string} testId 
 * @returns {Object} Test details
 */
export const getTestById = async (testId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.get(
      `${API_BASE_URL}/test/${testId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching test by ID:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to fetch test by ID');
  }
};

/**
 * Create Razorpay order for payment.
 * @param {string} studentId 
 * @param {number} amount 
 * @returns {Object} Order details from Razorpay
 */
export const createRazorpayOrder = async (studentId, amount, departmentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ studentId, amount, departmentId }),
    });

    if (!response.ok) {
      throw new Error('Failed to create order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};



/**
 * Verify Razorpay payment after successful transaction.
 * @param {string} orderId 
 * @param {string} paymentId 
 * @param {string} studentId
 * @returns {Object} Verification response
 */
export const verifyPaymentAndGenerateTest = async (orderId, paymentId, studentId, departmentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/verify-and-generate-test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orderId, paymentId, studentId, departmentId }),
    });

    if (!response.ok) {
      throw new Error('Failed to verify payment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

/**
 * Check a student's payment status
 * @param {string} studentId - The student's ID
 * @returns {Promise<Object>} - Payment status information
 */
export const checkPaymentRequirement = async (studentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/check-requirement/${studentId}`);
    
    if (!response.ok) {
      throw new Error('Failed to check payment requirement');
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking payment requirement:', error);
    throw error;
  }
};

export const submitTest = async (testId, studentId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return;

    const response = await axios.post(
      `${API_BASE_URL}/test/submit`,
      { testId, studentId },
      { headers }
    );

    return response.data;
  } catch (error) {
    console.error('Error submitting test:', error);

    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }

    throw new Error(error.response?.data?.message || 'Failed to submit test');
  }
};

export const getDepartments = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/mappings/departments`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const departments = await response.json();
    return departments;
  } catch (error) {
    console.error('Error fetching departments from mappings:', error);
    throw error;
  }
};

